import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { FilterSelectionMark } from "@/components/common/filter-selection-mark/filter-selection-mark";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { RecordCreator } from "@/types/api";
import { creatorDisplayName, creatorInitials } from "./creator-display";

interface CreatorPickerProps {
  creators: RecordCreator[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  /** Sizing for the picker root — the host controls overall height. */
  className?: string;
}

/**
 * Search input + scrollable, multi-select list of record creators. The search
 * stays pinned at the top and the list is the only scroll region, so it is
 * keyboard-safe when hosted in the mobile full-screen picker.
 */
export function CreatorPicker({
  creators,
  selectedIds,
  onToggle,
  className,
}: CreatorPickerProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return creators;
    return creators.filter(
      (creator) =>
        creatorDisplayName(creator).toLowerCase().includes(query) ||
        creator.email.toLowerCase().includes(query),
    );
  }, [creators, search]);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="shrink-0 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            aria-label="Search people"
            placeholder="Search people…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto px-3"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        {filtered.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-muted-foreground">
            No matches
          </p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((creator) => {
              const isSelected = selected.has(creator.id);
              return (
                <li key={creator.id}>
                  <button
                    type="button"
                    onClick={() => onToggle(creator.id)}
                    aria-pressed={isSelected}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
                      isSelected
                        ? "bg-accent text-accent-foreground dark:bg-selected dark:text-foreground"
                        : "hover:bg-accent hover:text-accent-foreground dark:text-muted-foreground dark:hover:bg-card-hover dark:hover:text-foreground",
                    )}
                  >
                    {/* Square: several creators can be ticked at once. */}
                    <FilterSelectionMark selected={isSelected} shape="square" />
                    <Avatar size="sm" className={cn(!isSelected && "dark:opacity-75")}>
                      {creator.avatar_url && (
                        <AvatarImage src={creator.avatar_url} alt="" />
                      )}
                      <AvatarFallback>
                        {creatorInitials(creator)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {creatorDisplayName(creator)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
